import { useEffect, useMemo } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Textarea } from "@chakra-ui/react";
import * as retailerLocationFields from "utils/schemas/retailerLocation";
import Form, { FormProps } from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import { RetailerLocationInput } from "__generated__/gql/graphql";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import AddressFieldset, {
  addressFieldsetSchema,
  AddressFieldsetType,
} from "components/Shared/Forms/AddressFieldset";
import CreatableRetailerSelect from "components/Shared/Select/CreatableRetailerSelect";
import NumberInput from "components/Shared/Forms/NumberInput";
import UrlInput from "components/Shared/Forms/UrlInput";

export const FORM_FIELDS = [
  "retailerId",
  "name",
  "storeNumber",
  "region",
  "website",
  "notes",
] as const;

export type FormFields = Pick<
  RetailerLocationInput,
  typeof FORM_FIELDS[number]
> & {
  address: AddressFieldsetType | null;
  distributionCenterIds: UUID[];
};

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  retailerId: retailerLocationFields.retailerId,
  name: retailerLocationFields.name,
  storeNumber: retailerLocationFields.storeNumber,
  region: retailerLocationFields.region,
  website: retailerLocationFields.website,
  notes: retailerLocationFields.notes,
  distributionCenterIds: retailerLocationFields.distributionCenterIds,
  address: addressFieldsetSchema.nullable().default(null),
});

const { FormField, ControlledFormField } =
  createFormFieldComponents<FormFields>({
    schema,
  });

export const processApolloError = makePostgraphileErrorProcessor<FormFields>(
  getPostgraphileErrorMatchersForFields<keyof FormFields>(FORM_FIELDS, {
    type: "RetailerLocation",
  }),
);

type Props = Omit<FormProps<FormFields, object>, "formContext"> & {
  // eslint-disable-next-line react/boolean-prop-naming
  autoFocus?: boolean;
  defaultValues?: Partial<FormFields>;
  isAdmin?: boolean;
  retailerId?: UUID;
};

export default function RetailerLocationForm({
  autoFocus,
  defaultValues: defaultValuesProp,
  children,
  isAdmin,
  retailerId,
  ...rest
}: Props) {
  const defaultValues = useMemo(() => {
    if (!retailerId) return defaultValuesProp;
    return { ...defaultValuesProp, retailerId };
  }, [defaultValuesProp, retailerId]);

  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      formContext.reset(defaultValues);
    }
  }, [defaultValues, formContext]);

  return (
    <Form {...{ formContext }} {...rest}>
      <ControlledFormField
        name="retailerId"
        isDisabled={!!retailerId}
        renderInput={({ field }) => (
          <CreatableRetailerSelect
            {...field}
            isAdmin={isAdmin}
            autoFocus={defaultValues?.retailerId ? undefined : autoFocus}
            isDisabled={!!retailerId}
          />
        )}
      />

      <FormField
        name="name"
        mt={0}
        autoFocus={defaultValues?.retailerId ? autoFocus : undefined}
      />

      <ControlledFormField
        name="storeNumber"
        renderInput={({ field }) => (
          <NumberInput
            {...field}
            max={2147483647}
            decimalScale={0}
            allowNegative={false}
            fontFamily="mono"
            fontWeight="normal"
          />
        )}
      />

      <ControlledFormField
        name="website"
        renderInput={({ field }) => <UrlInput {...field} allowPath />}
      />

      <FormField name="region" />

      <ControlledFormField
        name="distributionCenterIds"
        renderInput={({ field }) => (
          <DistributionCenterSelect
            {...field}
            isAddedToBrand={!isAdmin}
            isAdmin={isAdmin}
            isMulti
            placeholder="Select distribution centers…"
            noOptionsMessage={() => "No distribution centers found"}
          />
        )}
      />

      <FormField name="notes" InputComponent={Textarea} />

      <AddressFieldset<FormFields>
        fieldNamePrefix="address"
        legendLabel="Address"
        my={8}
      />

      {children}
    </Form>
  );
}
